/**
 * Google Ads conversion tag for the app.
 *
 * Loaded on the auth routes only — never on a page that renders merchant
 * financials. `send_page_view` is off for the same reason: app paths carry
 * workspace and entity UUIDs and there is no reason for Google to see them.
 *
 * See bizguard-backend/docs/CAMPAIGN_TRACKING_SPEC.md §6.2.
 */

const ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const SIGNUP_LABEL = import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL as string | undefined;

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

let loading = false;

export function isGoogleAdsConfigured(): boolean {
  return Boolean(ADS_ID);
}

/**
 * Injects gtag.js once. Safe to call on every auth-page mount.
 *
 * The conversion fires against `_gcl_aw`, which the marketing site's conversion
 * linker already wrote on `.zamino.com` — so this tag needs no click ID of its
 * own to attribute the signup.
 */
export function loadGoogleAdsTag(): void {
  if (!ADS_ID || loading || window.gtag) return;
  loading = true;

  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  gtag('config', ADS_ID, { send_page_view: false });
}

/**
 * Fires the `Signup complete` conversion. Called from both account-creation
 * paths — password and OAuth. No-op when the tag was never configured.
 *
 * The Ads conversion action is set to "Count: One", which is what makes a
 * duplicate call from a retry or a re-render harmless.
 */
export function trackSignupConversion(): void {
  if (!ADS_ID || !SIGNUP_LABEL || !window.gtag) return;

  window.gtag('event', 'conversion', {
    send_to: `${ADS_ID}/${SIGNUP_LABEL}`,
  });
}
